/* eslint-disable camelcase */
import { Box, Button, Chip, Divider, Menu, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { renderColorBox } from '../../utils/cssStyles';
import { fCurrency, fNumber } from '../../utils/formatNumber';
import { fDateMonth, fDateTime } from '../../utils/formatTime';
import Iconify from '../iconify';
import { USERS_COLUMNS_REPORT } from './permissionColumns';
import OpenInNewTabField from './OpenInNewTabField';
import { getProductIdLabel } from './dataGridHelper';

const MenuDropdown = ({ items, buttonLabel }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button size="small" color="black" onClick={handleClick} endIcon={<Iconify icon="eva:chevron-down-fill" />}>
        {buttonLabel}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()}>
        {items?.map((item, idx) => (
          <Typography variant="body2" sx={{ px: 1.5 }} key={idx}>
            {item?.valueName}
          </Typography>
        ))}
      </Menu>
    </>
  );
};

export { MenuDropdown };
const subcategory = {
  field: 'agoraSubcategory',
  headerName: 'Subcategory',
  width: 150,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const category = {
  field: 'agoraCategory',
  headerName: 'Category',
  width: 150,
  type: 'customSelect',
  renderCell: (params) => renderColorBox(params.row.agoraCategory, 'secondary'),
  valueGetter: (params) => params.row.agoraCategory || '',
};

const vendorItemId = {
  field: 'vendorItemId',
  headerName: 'Vendor Item Id',
  width: 150,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  valueGetter: (params) => params.value,
  type: 'customSelect',
};
const replacementSku = {
  field: 'replacementSku',
  headerName: 'Replacement Sku',
  width: 150,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  valueGetter: (params) => params.value,
  type: 'customSelect',
};
const replacementPrice = {
  field: 'replacementPrice',
  headerName: 'Replacement Price',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.row.replacementPrice || 0,
};
const replacementDescription = {
  field: 'replacementDescription',
  headerName: 'Replacement Description',
  width: 200,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};
const description = {
  field: 'description',
  headerName: 'Description',
  width: 200,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};
const issue = {
  field: 'issue',
  headerName: 'Issue',
  width: 300,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        fontSize: '0.70rem',
      }}
    >
      {params.value}
    </Box>
  ),
  valueGetter: (params) => params.value,
  type: 'customSelect',
};
const manufacturer = {
  field: 'manufacturer',
  headerName: 'Manufacturer',
  width: 150,
  type: 'customSelect',
};
const manufacturerId = {
  field: 'manufacturerId',
  headerName: 'Manufacturer Id',
  width: 150,
  type: 'customSelect',
};
const agoraNumber = {
  field: 'agoraNumber',
  headerName: 'PS ID',
  width: 150,
  type: 'customSelect',
};
const id = {
  field: 'id',
  headerName: 'PS ID',
  width: 150,
  type: 'customSelect',
};
const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  width: 150,
  type: 'customSelect',
};
const orderNumber = {
  field: 'orderNumber',
  headerName: 'Order #',
  width: 150,
  type: 'customSelect',
  renderCell: (params) =>
    params.row.orderId > 0 ? (
      <OpenInNewTabField value={params.value} url={`/dashboard/orders/${params.row.orderId}`} />
    ) : (
      params.value
    ),
};
const reportType = {
  field: 'reportName',
  headerName: 'Report Name',
  minWidth: 100,
  flex: 1,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        fontSize: '0.70rem',
      }}
    >
      {params.value}
    </Box>
  ),
};
const reportName = {
  field: 'name',
  headerName: 'Title',
  minWidth: 100,
  flex: 1,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        fontSize: '0.70rem',
      }}
    >
      {params.value}
    </Box>
  ),
};
const facilities = {
  field: 'facility',
  headerName: 'Facility',
  width: 140,
  renderCell: (params) => {
    const facilities = params.row.listScheduleOptions?.filter((o) => o.name === 'Facilities');
    const facilityCount = facilities?.length;
    return (
      <Box>
        {facilityCount < 1 ? (
          `Add a facility`
        ) : facilityCount === 1 ? (
          facilities[0]?.valueName
        ) : (
          <MenuDropdown items={facilities} buttonLabel={`${facilityCount} facilities`} />
        )}
      </Box>
    );
  },
  valueGetter: (params) =>
    params.row.listScheduleOptions
      ?.filter((o) => o.name === 'Facilities')
      ?.map((o) => o.valueName)
      ?.join(', '),
  type: 'customSelect',
  getFilterValues: (row) => row.listScheduleOptions?.filter((o) => o.name === 'Facilities')?.map((o) => o.valueName),
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
};
const facility = {
  field: 'facility',
  headerName: 'Facility',
  width: 140,
  renderCell: (params) => <Box>{params.row.facility}</Box>,
  type: 'customSelect',
};

const company = {
  field: 'company',
  headerName: 'Group',
  width: 140,
  renderCell: (params) => <Box>{params.row.company}</Box>,
  type: 'customSelect',
};
const groups = {
  field: 'group',
  headerName: 'Group',
  width: 120,
  renderCell: (params) => {
    if (!params.row.hasCompaniesOption) return <Box />;
    const groups = params.row.listScheduleOptions?.filter((o) => o.name === 'Companies');
    const groupCount = groups.length;
    return (
      <Box>
        {groupCount < 1 ? (
          `Add a group`
        ) : groupCount === 1 ? (
          groups[0]?.valueName
        ) : (
          <MenuDropdown items={groups} buttonLabel={`${groupCount} groups`} />
        )}
      </Box>
    );
  },
  valueGetter: (params) =>
    params.row.listScheduleOptions
      ?.filter((o) => o.name === 'Companies')
      ?.map((o) => o.valueName)
      ?.join(', '),
  type: 'customSelect',
  getFilterValues: (row) => row.listScheduleOptions?.filter((o) => o.name === 'Companies')?.map((o) => o.valueName),
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
};

const group = {
  field: 'group',
  headerName: 'Group',
  width: 120,
  type: 'customSelect',
};
const amount = {
  field: 'amount',
  headerName: 'Qty',
  width: 50,
  type: 'customNumber',
};
const uom = {
  field: 'uom',
  headerName: 'UOM',
  width: 150,
  type: 'customSelect',
};
const baseUom = {
  field: 'baseUOM',
  headerName: 'Base Uom',
  width: 150,
  type: 'customSelect',
};
const uomAmount = {
  field: 'uomAmount',
  headerName: 'UOM Amount',
  width: 150,
  type: 'customSelect',
};
const replacementUomAmount = {
  field: 'replacementUomAmount',
  headerName: 'Replacement UOM Amount',
  width: 150,
  type: 'customSelect',
};
const uomType = {
  field: 'uomType',
  headerName: 'UOM Type',
  width: 150,
  type: 'customSelect',
};

const serialNumber = {
  field: 'serialNumber',
  headerName: 'Serial Number',
  width: 150,
  type: 'customSelect',
};

const rentalItemDescription = {
  field: 'description',
  headerName: 'Description',
  width: 150,
  type: 'customSelect',
};
const personFor = {
  field: 'residentFor',
  headerName: 'Person For',
  width: 150,
  type: 'customSelect',
};

const matched = {
  field: 'marker',
  headerName: 'Match',
  width: 150,
  type: 'customSelect',
  renderCell: (params) => (
    <Box>
      {params.value === 'Matched' ? (
        <Typography
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '10%', padding: '6px' }}
          variant="body2"
          color="success.dark"
        >
          {params.value}
          <Iconify icon="icon-park-outline:check-one" />
        </Typography>
      ) : (
        <Typography
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '10%', padding: '6px' }}
          variant="body2"
          color="warning.dark"
        >
          {params.value}
          <Iconify icon="si:warning-line" />
        </Typography>
      )}
    </Box>
  ),
};
const replacementUomType = {
  field: 'replacementUomType',
  headerName: 'Replacement UOM Type',
  width: 150,
  type: 'customSelect',
};
const todaysPrice = {
  field: 'mostRecentPrice',
  headerName: 'Most Recent Price',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.row.mostRecentPrice || 0,
};

const item_vendorItemId = {
  field: 'vendorItemId',
  headerName: 'Vendor Item Id',
  width: 150,
  path: 'item.vendorItemId',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.item?.vendorItemId}
    </Box>
  ),
  valueGetter: (params) => params?.row?.item?.vendorItemId,
  type: 'customSelect',
};

//item_description
const item_description = {
  field: 'description',
  headerName: 'Description',
  path: 'item.description',
  width: 200,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.item?.description}
    </Box>
  ),

  valueGetter: (params) => params?.row?.item?.description,
};
//order_description
const order_description = {
  field: 'description',
  headerName: 'Ordered Description',
  path: 'orderItem.description',
  width: 200,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.orderItem?.description}
    </Box>
  ),
  valueGetter: (params) => params?.row?.orderItem?.description,
};
const order_qty = {
  field: 'quantity',
  headerName: 'Qty Ord.',
  width: 100,
  type: 'customNumber',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.orderItem?.quantity}
    </Box>
  ),
  valueGetter: (params) => params?.row?.orderItem?.quantity,
};
const order_price = {
  field: 'price',
  headerName: 'Price Ord.',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {fCurrency(params?.row?.orderItem?.price)}
    </Box>
  ),
  valueGetter: (params) => params?.row?.orderItem?.price,
};
const order_uomType = {
  field: 'uomType',
  headerName: 'UOM Ord.',
  width: 80,
  type: 'customSelect',
  path: 'orderItem.uomType',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.orderItem?.uomType}
    </Box>
  ),
  valueGetter: (params) => params?.row?.orderItem?.uomType,
};
const invoiceItems = {
  field: 'invoiceItems',
  headerName: 'Invoice Items',
  width: 500,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.invoiceItems?.map((item, index) => (
        <div key={index}>
          {index !== 0 && <Divider sx={{ width: '100%', my: 0.5, backgroundColor: '#000' }} />}
          <div>
            Received: {fDateTime(item.dateInvoiceReceived)}. Invoiced: {fDateTime(item.dateInvoiceReceived)}
          </div>
          <div>{item.description}</div>
          <div>
            {fCurrency(item.price)}. Qty: {fNumber(item.qty)}. UOM: {item.uomType}{' '}
          </div>
          <div>
            Invoice Id: {item.vendorInvoiceId}. Sent: {item.sent ? 'True' : 'False'}
          </div>
        </div>
      ))}
    </Box>
  ),
  valueGetter: (params) =>
    params?.row?.invoiceItems
      ?.map(
        (item) => `${item.description} ${item.price} ${item.qty} ${item.uomType} ${item.vendorInvoiceId} ${item.sent}`
      )
      .join(' '),
};

// item_manufacturer,

const item_manufacturer = {
  field: 'manufacturer',
  headerName: 'Manufacturer',
  width: 150,
  type: 'customSelect',
  path: 'item.manufacturer',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.item?.manufacturer}
    </Box>
  ),
  valueGetter: (params) => params?.row?.item?.manufacturer,
};

// item_manufacturerId,
// item_category,
// item_subcategory,
// item_agoraNumber,
// item_vendor,
// item_amount,
// item_uom,
// item_todaysPrice,

const item_manufacturerId = {
  field: 'manufacturerId',
  headerName: 'Manufacturer Id',
  width: 150,
  type: 'customSelect',
  path: 'item.manufacturerId',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.item?.manufacturerId}
    </Box>
  ),
  valueGetter: (params) => params?.row?.item?.manufacturerId,
};

const item_category_new = {
  field: 'category',
  headerName: 'Category',
  width: 150,
  type: 'customSelect',
  renderCell: (params) => renderColorBox(params?.row?.item?.category, 'secondary'),
  valueGetter: (params) => params?.row?.item?.category || '',
  path: 'item.category',
};

const item_subcategory_new = {
  field: 'subcategory',
  headerName: 'Subcategory',
  width: 150,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.item?.subcategory}
    </Box>
  ),
  valueGetter: (params) => params?.row?.item?.subcategory || '',
  path: 'item.subcategory',
};

const item_agoraNumber_new = {
  field: 'psNumber',
  headerName: 'PS ID',
  width: 150,
  type: 'customSelect',
  getFilterValues: (row) => [getProductIdLabel(row?.item?.productId)],
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {getProductIdLabel(params?.row?.item?.productId)}
    </Box>
  ),
  valueGetter: (params) => getProductIdLabel(params?.row?.item?.productId),
};

const item_vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  width: 150,
  type: 'customSelect',
  path: 'item.vendor',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params?.row?.item?.vendor}
    </Box>
  ),
  valueGetter: (params) => params?.row?.item?.vendor,
};

const item_price = {
  field: 'price',
  headerName: 'Price',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params?.row.item?.price),
  valueGetter: (params) => params?.row.item?.price || 0,
};

const item_uomAmount = {
  field: 'uomAmount',
  headerName: 'UOM',
  width: 150,
  minWidth: 70,
  renderCell: (params) => params.value,
  valueGetter: (params) => `${params.row.item.uomAmount ? params.row.item.uomAmount : ''}/${params.row.item.uomType}`,
  type: 'customSelect',
  getFilterValues: (row) => [`${row.item.uomAmount ? row.item.uomAmount : ''}/${row.item.uomType}`],
};
const perItem = {
  field: 'perItem',
  headerName: 'Per Item',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params?.row?.perItem),
  valueGetter: (params) => params?.row?.perItem || 0,
};

const price = {
  field: 'price',
  headerName: 'Price',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params?.row?.price),
  valueGetter: (params) => params?.row?.price || 0,
};
const blankPrice = {
  field: 'price',
  headerName: 'Price',
  width: 150,
  renderCell: () => '',
  valueGetter: () => '',
};
const blankQuantity = {
  field: 'quantity',
  headerName: 'Quantity',
  width: 150,
  renderCell: () => '',
  valueGetter: () => '',
};
const total = {
  field: 'total',
  headerName: 'Total',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params?.row?.total),
  valueGetter: (params) => params?.row?.total || 0,
};
const originalPrice = {
  field: 'originalPrice',
  headerName: 'Original Price',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params?.row?.originalPrice),
  valueGetter: (params) => params?.row?.originalPrice || 0,
};
const dateApproved = {
  field: 'dateApproved',
  headerName: 'Date Approved',
  width: 180,
  type: 'customDate',
  renderCell: (params) => fDateTime(params.value),
  valueGetter: (params) => params.value || 0,
};
const frequency = {
  field: 'frequency',
  headerName: 'Frequency',
  width: 180,
  type: 'customSelect',
};
const facilityCode = {
  field: 'facilityCode',
  headerName: 'Facility Account',
  width: 80,
  type: 'customSelect',
};
const glCode = {
  field: 'glCode',
  headerName: 'Gl Code',
  width: 80,
  type: 'customSelect',
};
const facilityName = {
  field: 'facilityName',
  headerName: 'Facility',
  width: 150,
  type: 'customSelect',
};
//budget
//spent
// fullSpent
//census
//ppdBudget
//ppdSpent

const budget = {
  field: 'budget',
  headerName: 'Budget',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const spent = {
  field: 'spent',
  headerName: 'Spent',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const fullSpent = {
  field: 'fullSpent',
  headerName: 'Full Spent',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const census = {
  field: 'census',
  headerName: 'Census',
  width: 150,
  type: 'customNumber',
  renderCell: (params) => fNumber(params.value),
  valueGetter: (params) => params.value || 0,
};
const dailyCensus = {
  field: 'dailyCensus',
  headerName: 'Daily Census',
  width: 150,
  type: 'customNumber',
  renderCell: (params) => fNumber(params.value),
  valueGetter: (params) => params.value || 0,
};
const totalCensus = {
  field: 'totalCensus',
  headerName: 'Total Census',
  width: 150,
  type: 'customNumber',
  renderCell: (params) => fNumber(params.value),
  valueGetter: (params) => params.value || 0,
};
const ppd = {
  field: 'ppd',
  headerName: 'PPD',
  width: 150,
  type: 'customNumber',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const ppdBudget = {
  field: 'ppdBudget',
  headerName: 'PPD Budget',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const flatBudget = {
  field: 'flatRate',
  headerName: 'Flat Budget',
  width: 150,
  type: 'customSelect',
  valueGetter: (params) => (params.row.flatRate ? 'Yes' : 'No'),
  renderCell: (params) => params.value,
  getFilterValues: (row) => [row.flatRate ? 'Yes' : 'No'],
};

const excludeFromMain = {
  field: 'excludeFromMain',
  headerName: 'Exclude From Main',
  width: 180,
  type: 'customSelect',
  valueGetter: (params) => (params.row.excludeFromMain ? 'Yes' : 'No'),
  renderCell: (params) => params.value,
  getFilterValues: (row) => [row.excludeFromMain ? 'Yes' : 'No'],
};

const ppdSpent = {
  field: 'ppdSpent',
  headerName: 'PPD Spent',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const invoiceId = {
  field: 'invoiceId',
  headerName: 'Invoice Id',
  width: 150,
  type: 'customSelect',
  renderCell: (params) => (
    <OpenInNewTabField
      value={params.value?.toString()}
      url={`/dashboard/invoicing/${params.row.sent ? 'previous' : 'pending'}/${params.row.invoiceId}`}
    />
  ),
};

const orderNum = {
  field: 'orderNumber',
  headerName: 'Order#',
  width: 150,
  type: 'customSelect',
  renderCell: (params) =>
    params.row.orderId > 0 ? (
      <OpenInNewTabField value={params.value} url={`/dashboard/orders/${params.row.orderId}`} />
    ) : (
      params.value
    ),
};

const vendorInvoiceId = {
  field: 'vendorInvoiceId',
  headerName: 'Vendor Id',
  width: 150,
  type: 'customSelect',
};

const expectedPrice = {
  field: 'expectedPrice',
  headerName: 'Expected Price',
  width: 150,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const quantity = {
  field: 'quantity',
  headerName: 'Sent',
  width: 50,
  type: 'customNumber',
};

const qty = {
  field: 'quantity',
  headerName: 'QTY',
  width: 50,
  type: 'customNumber',
};
const tax = {
  field: 'taxAmount',
  headerName: 'Tax',
  width: 50,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.value),
  valueGetter: (params) => params.value || 0,
};

const quantityPriceTotal = {
  field: 'quantityPriceTotal',
  headerName: 'Total',
  width: 50,
  valueGetter: (params) => (params.row?.quantity ?? 0) * (params.row?.price ?? 0) + (params.row?.taxAmount ?? 0),
  renderCell: (params) =>
    fCurrency((params.row?.quantity ?? 0) * (params.row?.price ?? 0) + (params.row?.taxAmount ?? 0)),
  type: 'customNumber',
};

const expectedQuantity = {
  field: 'expectedQuantity',
  headerName: 'Ordered',
  width: 50,
  type: 'customNumber',
};

const account = {
  field: 'account',
  headerName: 'Account',
  width: 150,
  type: 'customSelect',
};
const approvedBy = {
  field: 'approvedBy',
  headerName: 'Approved By',
  width: 150,
  type: 'customSelect',
};
const submittedBy = {
  field: 'submittedBy',
  headerName: 'Submitted By',
  width: 150,
  type: 'customSelect',
};
const invoiceDate = {
  field: 'invoiceDate',
  headerName: 'Invoice Date',
  width: 150,
  type: 'customDate',
  renderCell: (params) => fDateTime(params.value),
  valueGetter: (params) => params.value || 0,
};
const date = {
  field: 'date',
  headerName: 'Date',
  width: 150,
  type: 'customDate',
  renderCell: (params) => fDateMonth(params.value),
  valueGetter: (params) => params.value || 0,
};
const dateOrdered = {
  field: 'dateOrdered',
  headerName: 'Date Ordered',
  width: 180,
  type: 'customDate',
  renderCell: (params) => fDateTime(params.value),
  valueGetter: (params) => params.value || 0,
};
const budgetDate = {
  field: 'budgetDate',
  headerName: 'Budget Date',
  width: 180,
  type: 'customDate',
  renderCell: (params) => (params.value ? fDateMonth(params.value) : ''),
  valueGetter: (params) => params.value || 0,
};
const sent = {
  field: 'sent',
  headerName: 'Sent',
  width: 100,
  // <Iconify icon="mdi:check" color="primary" width={28} height={28} />;
  renderCell: (params) => (params.value ? <Iconify icon="mdi:check" color="primary" width={28} height={28} /> : ''),
  valueGetter: (params) => params.value || false,
};

const item = {
  field: 'item',
  headerName: 'Item',
  width: 150,
  type: 'customSelect',
};

const invoiceIds = {
  field: 'invoices',
  headerName: 'Invoices',
  width: 300,
  valueGetter: (params) => params.value?.map((v) => v?.vendorInvoiceId)?.join(', ') ?? '',
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.invoices?.map((invoice) => invoice.vendorInvoiceId) || [],
  renderCell: (params) => {
    const { invoices } = params.row;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, my: 0.5, width: '100%' }}>
        {invoices?.map((invoice) => {
          const { total, sent, vendorInvoiceId, invoiceId } = invoice;
          return (
            <OpenInNewTabField
              key={vendorInvoiceId}
              value={
                <Chip
                  component="span"
                  clickable
                  label={`${vendorInvoiceId} | Total: ${fCurrency(total)}, Booked: ${sent ? 'Yes' : 'No'}`}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                />
              }
              url={`/dashboard/invoicing/${sent ? 'previous' : 'pending'}/${invoiceId}`}
            />
          );
        })}
      </Box>
    );
  },
  type: 'customSelect',
};

const getPoTotal = (orderItems) => orderItems?.reduce((acc, item) => acc + item.price * item.quantity, 0);

const poTotal = {
  field: 'orderItems',
  headerName: 'PO Total',
  width: 150,
  valueGetter: (params) => getPoTotal(params.row?.orderItems) ?? 0,
  renderCell: (params) => (getPoTotal(params.row?.orderItems) ? fCurrency(getPoTotal(params.row?.orderItems)) : ''),
  type: 'customMoney',
};

const getInvoicedTotal = (invoices) => invoices?.reduce((acc, item) => acc + item.total, 0);

const invoiceTotal = {
  field: 'invoiceTotal',
  headerName: 'Invoiced Total',
  width: 150,
  valueGetter: (params) => getInvoicedTotal(params.row?.invoices) ?? 0,
  renderCell: (params) =>
    getInvoicedTotal(params.row?.invoices) ? fCurrency(getInvoicedTotal(params.row?.invoices)) : '',
  type: 'customMoney',
};

const invoiced = {
  field: 'invoiced',
  headerName: 'Invoiced',
  type: 'customSelect',
  valueGetter: (params) => (params.row.invoiced ? 'Yes' : 'No'),
  renderCell: (params) => params.value,
  getFilterValues: (row) => [row.invoiced ? 'Yes' : 'No'],
};
const onOriginalPO = {
  field: 'orderItem',
  headerName: 'On Original PO',
  type: 'customSelect',
  valueGetter: (params) => (params.row?.orderItem.quantity > 0 ? 'Yes' : 'No'),
  renderCell: (params) => (params.row?.orderItem.quantity ? 'Yes' : 'No'),
  getFilterValues: (row) => [row?.orderItem.quantity > 0 ? 'Yes' : 'No'],
};
const hasCensusIntegration = {
  field: 'hasIntegration',
  headerName: 'Has Integration',
  type: 'customSelect',
  getFilterValues: (row) => [row.hasIntegration ? 'Yes' : 'No'],
  valueGetter: (params) => (params.value ? 'Yes' : 'No'),
  renderCell: (params) =>
    params.row.hasIntegration ? <Iconify icon="mdi:check" color="primary" width={28} height={28} /> : '',
};

const censusIntegrationType = {
  field: 'integrationType',
  headerName: 'Integration',
  type: 'customSelect',
};

const orgCode = {
  field: 'orgCode',
  headerName: 'PCC Org Code',
  type: 'customSelect',
};

const lastIntegratedCensus = {
  field: 'lastIntegratedCensusNumber',
  headerName: 'Last Integrated Census',
  type: 'customNumber',
  renderCell: (params) => (params.value ? fNumber(params.value) : null),
  valueGetter: (params) => params.value,
};

const defaultCensusNumber = {
  field: 'defaultCensusNumber',
  headerName: 'Default Census',
  type: 'customNumber',
  renderCell: (params) => (params.value ? fNumber(params.value) : null),
  valueGetter: (params) => params.value,
};

const lastIntegratedCensusDate = {
  field: 'lastIntegratedCensusDate',
  headerName: 'Last Integrated Census Date',
  type: 'customDate',
  renderCell: (params) => fDateMonth(params.value),
  valueGetter: (params) => params.value || 0,
};
const itemDescription = {
  field: 'itemDescription',
  headerName: 'Item Description',
  type: 'customSelect',
};
const averagePrice = {
  field: 'averagePrice',
  headerName: 'Average Price',
  renderCell: (params) => (params.value ? fCurrency(params.value) : '$0.00'),
  type: 'customMoney',
};
const uomData = {
  field: 'uomData',
  headerName: 'UOM Amount/Type',
  type: 'customSelect',
};
const numPriceChanges = {
  field: 'numPriceChanges',
  headerName: '# of Price Changes',
  type: 'customNumber',
};
const itemName = {
  field: 'itemName',
  headerName: 'Description',
  type: 'customSelect',
};
const vendorName = {
  field: 'vendorName',
  headerName: 'Vendor',
  type: 'customSelect',
};
const rejectedCount = {
  field: 'rejectedCount',
  headerName: 'Rejected Count',
  type: 'customNumber',
};
const productId = {
  field: 'productId',
  headerName: 'PS ID',
  type: 'customSelect',
  renderCell: (params) => params.value,
  valueGetter: (params) => getProductIdLabel(params.value),
  getFilterValues: (row) => {
    const value = getProductIdLabel(row?.productId);
    return value ? [value] : [];
  },
};
const status = {
  field: 'status',
  headerName: 'Status',
  type: 'customSelect',
  defaultHidden: true,
};

const formatTransportReportDate = (value) => {
  if (!value) return '';
  const datePart = String(value).split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart || '';
  return format(new Date(`${datePart}T00:00:00`), 'MMM dd, yyyy');
};

const formatTransportReportTime = (value) => {
  if (!value) return '';
  const timePart = String(value).split('T')[1]?.slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(timePart || '')) return timePart || '';
  return format(new Date(`1970-01-01T${timePart}:00`), 'h:mm a');
};

const appointmentDate = {
  field: 'appointmentDateTime',
  headerName: 'Appt Date',
  type: 'customDate',
  width: 150,
  renderCell: (params) => formatTransportReportDate(params.row.appointmentDateTime),
  valueGetter: (params) => params.row.appointmentDateTime || '',
};
const appointmentTime = {
  field: 'appointmentTime',
  headerName: 'Appt Time',
  width: 100,
  renderCell: (params) => formatTransportReportTime(params.row.appointmentDateTime),
  valueGetter: (params) => params.row.appointmentDateTime || '',
};
const pickupTime = {
  field: 'pickupTime',
  headerName: 'Pickup Time',
  width: 100,
  renderCell: (params) => formatTransportReportTime(params.row.pickupTime),
  valueGetter: (params) => params.row.pickupTime || '',
};
const seenByDate = {
  field: 'seenByDate',
  headerName: 'Seen By Date',
  type: 'customDate',
  width: 150,
  renderCell: (params) => formatTransportReportDate(params.row.seenByDate),
  valueGetter: (params) => params.row.seenByDate || '',
};

const transportSubmittedBy = {
  field: 'submittedBy',
  headerName: 'Submitted By',
  width: 180,
  defaultHidden: true,
  type: 'customSelect',
};

const pickupLocationName = {
  field: 'pickupLocationName',
  headerName: 'Pickup Location Name',
  width: 160,
  type: 'customSelect',
};

const pickupLocationAddress = {
  field: 'pickupLocationAddress',
  headerName: 'Pickup Location Address',
  width: 250,
  defaultHidden: true,
  type: 'customSelect',
};

const dropoffLocationName = {
  field: 'dropoffLocationName',
  headerName: 'Dropoff Location Name',
  width: 160,
  type: 'customSelect',
};

const dropoffLocationAddress = {
  field: 'dropoffLocationAddress',
  headerName: 'Dropoff Location Address',
  width: 250,
  defaultHidden: true,
  type: 'customSelect',
};

const driver = {
  field: 'driver',
  headerName: 'Driver',
  width: 180,
  defaultHidden: true,
  type: 'customSelect',
};

const isOutsourced = {
  field: 'isOutsourced',
  headerName: 'Outsourced?',
  width: 130,
  defaultHidden: true,
  type: 'customSelect',
  valueGetter: (params) => (params.value === true ? 'Yes' : params.value === false ? 'No' : ''),
  renderCell: (params) => params.value,
  getFilterValues: (row) => (row.isOutsourced === true ? ['Yes'] : row.isOutsourced === false ? ['No'] : []),
};

const residentNames = {
  field: 'residentNames',
  headerName: 'Resident',
  width: 200,
  type: 'customSelect',
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {Array.isArray(params.value) ? params.value[0] : params.value}
    </Box>
  ),
  getFilterValues: (row) => {
    if (Array.isArray(row.residentNames)) return row.residentNames.filter(Boolean);
    return row.residentNames ? [row.residentNames] : [];
  },
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  valueGetter: (params) => (Array.isArray(params.row.residentNames) ? params.row.residentNames[0] : ''),
};

const doctorType = {
  field: 'doctorType',
  headerName: 'Doctor Type',
  width: 160,
  type: 'customSelect',
};

const tripCount = {
  field: 'tripCount',
  headerName: 'Trip Count',
  width: 160,
  type: 'customSelect',
};

const USERS_COLUMNS = [
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    type: 'customSelect',
  },
  ...USERS_COLUMNS_REPORT,
  {
    field: 'roles',
    headerName: 'Roles',
    flex: 3,
    valueGetter: (params) => params.row.roles?.map((role) => role.name).join(', ') || '',
    renderCell: (params) => {
      const { value } = params;
      if (!value) return null;
      return (
        <Box component="div" sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', py: 0.5 }}>
          {value?.split(', ')?.map((role, index) => (
            <Chip sx={{ mt: 0.5, mr: 0.5 }} key={index} label={role} size="small" />
          ))}
        </Box>
      );
    },
    type: 'customSelect',
    getFilterValues: (row) => row.roles?.map((val) => val.name) || [],
    customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  },
];

const PURCHASING_ITEMS_ORDERED = [
  vendorItemId,
  description,
  manufacturer,
  manufacturerId,
  category,
  subcategory,
  agoraNumber,
  vendor,
  amount,
  uom,
  baseUom,
  todaysPrice,
];
const ITEMS_ORDERED_FULL = [
  orderNumber,
  vendorItemId,
  description,
  manufacturer,
  manufacturerId,
  category,
  subcategory,
  agoraNumber,
  vendor,
  uom,
  baseUom,
  price,
  qty,
  total,
  facility,
  glCode,
  census,
  ppd,
  dateOrdered,
  dateApproved,
  budgetDate,
  approvedBy,
  submittedBy,
];
const FACILITIES_INVOICES_REPORT = [
  orderNum,
  invoiceId,
  vendorInvoiceId,
  invoiceDate,
  facility,
  vendorItemId,
  description,
  price,
  expectedPrice,
  quantity, // sent
  expectedQuantity, // Sent
  quantityPriceTotal,
  tax,
  category,
  subcategory,
  vendor,
];
const FACILITIES_INVOICES_DISCREPANCY = [
  vendorInvoiceId,
  vendor,
  facility,
  account,
  sent,
  invoiceDate,
  item,
  issue,
  sent,
];
const INVOICES_ISSUES_REPORT = [vendorInvoiceId, vendor, issue];
const TOP_LEVEL_BUDGET_REPORT = [facilityName, budget, spent, fullSpent, census, ppdBudget, ppdSpent];
const FACILITY_COMPARISON_REPORT = [
  item_description,
  item_vendorItemId,
  item_manufacturer,
  item_manufacturerId,
  item_category_new,
  item_subcategory_new,
  item_agoraNumber_new,
  item_vendor,
  item_price,
];
const FACILITY_HISTORY_REPORT = [
  item_description,
  item_vendorItemId,
  item_manufacturer,
  item_manufacturerId,
  item_category_new,
  item_subcategory_new,
  item_agoraNumber_new,
  item_vendor,
  item_price,
  item_uomAmount,
];
const PO_INVOICE_REPORT = [
  orderNumber,
  facility,
  facilityCode,
  vendor,
  dateOrdered,
  dateApproved,
  budgetDate,
  vendorItemId,
  order_description,
  order_qty,
  order_price,
  order_uomType,
  invoiceItems,
  invoiced,
  onOriginalPO,
];
const PO_INVOICE_CONSOLIDATED_REPORT = [
  orderNumber,
  facility,
  vendor,
  dateOrdered,
  dateApproved,
  budgetDate,
  invoiceIds,
  invoiced,
  poTotal,
  invoiceTotal,
];
const SUBSTITUTE_REPORT = [
  description,
  category,
  subcategory,
  orderNumber,
  dateApproved,
  vendor,
  vendorItemId,
  originalPrice,
  uomAmount,
  uomType,
  replacementSku,
  replacementPrice,
  replacementDescription,
  replacementUomAmount,
  replacementUomType,
];
const VENDOR_COMPARISON_REPORT = [description, id, category, subcategory];
const CENSUS_INTEGRATION_REPORT = [
  facility,
  group,
  defaultCensusNumber,
  hasCensusIntegration,
  censusIntegrationType,
  orgCode,
  lastIntegratedCensus,
  lastIntegratedCensusDate,
];
const PRICE_INCREASE_REPORT = [vendor, facility, itemDescription, vendorItemId, averagePrice, uomData, numPriceChanges];
const SERIAL_NUMBER_MATCHUP_REPORT = [serialNumber, matched, rentalItemDescription, orderNumber, facility, personFor];
const REJECTED_ITEMS_BY_VENDOR_REPORT = [
  { ...vendorItemId, flex: 1 },
  { ...itemName, flex: 1 },
  { ...vendorName, flex: 1 },
  { ...category, flex: 1 },
  { ...subcategory, flex: 1 },
  { ...productId, flex: 1 },
  { ...rejectedCount, flex: 1 },
];

const CENSUS_REPORT = [group, facilityName, date, dailyCensus, totalCensus];
const SCHEDULED_REPORTS = [reportType, reportName, groups, facilities, frequency];
const BID_SHEET_REPORT = [category, subcategory, agoraNumber, description, blankPrice, blankQuantity];
const BUDGETS_REPORT = [company, facility, category, subcategory, glCode, ppdBudget, flatBudget, excludeFromMain];
const TRANSPORT_REQUESTS_REPORT = [
  facility,
  appointmentDate,
  appointmentTime,
  pickupTime,
  seenByDate,
  status,
  residentNames,
  transportSubmittedBy,
  pickupLocationName,
  pickupLocationAddress,
  dropoffLocationName,
  dropoffLocationAddress,
  driver,
  isOutsourced,
];
const TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT = [facility, doctorType, tripCount];

const TRANSPORT_REQUESTS_DEFAULT_HIDDEN_COLUMNS = TRANSPORT_REQUESTS_REPORT.filter(
  (column) => column.defaultHidden
).map((column) => column.field);

export {
  FACILITIES_INVOICES_DISCREPANCY,
  FACILITIES_INVOICES_REPORT,
  FACILITY_COMPARISON_REPORT,
  FACILITY_HISTORY_REPORT,
  INVOICES_ISSUES_REPORT,
  ITEMS_ORDERED_FULL,
  PO_INVOICE_CONSOLIDATED_REPORT,
  PO_INVOICE_REPORT,
  PURCHASING_ITEMS_ORDERED,
  SCHEDULED_REPORTS,
  SUBSTITUTE_REPORT,
  TOP_LEVEL_BUDGET_REPORT,
  USERS_COLUMNS,
  VENDOR_COMPARISON_REPORT,
  CENSUS_INTEGRATION_REPORT,
  PRICE_INCREASE_REPORT,
  REJECTED_ITEMS_BY_VENDOR_REPORT,
  BID_SHEET_REPORT,
  SERIAL_NUMBER_MATCHUP_REPORT,
  BUDGETS_REPORT,
  CENSUS_REPORT,
  TRANSPORT_REQUESTS_REPORT,
  TRANSPORT_REQUESTS_DEFAULT_HIDDEN_COLUMNS,
  TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT,
};
